import { normalize, schema } from "normalizr";
import { camelizeKeys } from "humps";
import { compose, ifElse, omit, assoc, evolve } from "ramda";

import { get, post } from "api";
import { renameKeys, fromArrayOfIdAndValue } from "cxa-utils";

const API_ROOT = "/";

// Fetches an API response and normalizes the result JSON according to schema.
// This makes every API response have the same shape, regardless of how nested it was.
const callApi = (
  method,
  endpoint,
  payload,
  schema,
  postApiCallHook,
  postNormalizationHook
) => {
  const fullUrl = endpoint.indexOf(API_ROOT) === -1
    ? API_ROOT + endpoint
    : endpoint;
  const req = method === "post" ? post(fullUrl, payload) : get(fullUrl);
  return req.then(response => {
    if (!response.ok) {
      return Promise.reject(response.body);
    }

    let data = camelizeKeys(response.body);
    data = postApiCallHook ? postApiCallHook(data) : data;
    data = schema ? normalize(data, schema) : data;
    data = postNormalizationHook ? postNormalizationHook(data) : data;
    return data;
  });
};

const insurancePlanSchema = new schema.Entity("insurancePlans");

// planId is only unique to the product
// We create a unique ID for plans across all products by concatenating productId and planId
const insuranceProductSchema = new schema.Entity(
  "insuranceProducts",
  {},
  {
    idAttribute: "productId",
    processStrategy: value =>
      assoc(
        "plans",
        value.plans.map(plan =>
          assoc("id", `${value.productId}|${plan.planId}`, plan)
        ),
        value
      ),
  }
);
insuranceProductSchema.define({ plans: [insurancePlanSchema] });

const renameKeysForInsuranceProductForm = renameKeys({
  formID: "formId",
  productID: "productId",
});
const insuranceProductFormSchema = new schema.Entity(
  "insuranceProductForms",
  {},
  {
    idAttribute: value => value.formId || value.formID,
    processStrategy: renameKeysForInsuranceProductForm,
  }
);

const insuranceProductsSchema = { products: [insuranceProductSchema] };

const insuranceCartItemsSchema = new schema.Entity(
  "insuranceCartItems",
  {},
  {
    idAttribute: "cartItemId",
    processStrategy: value =>
      compose(
        assoc("id", `${value.productId}|${value.planId}`),
        ifElse(
          x =>
            !x.insuredEntityDetails ||
            (x.insuredEntityDetails && x.insuredEntityDetails.length === 0),
          omit("insuredEntityDetails"),
          compose(
            evolve({
              insuredEntityDetails: fromArrayOfIdAndValue,
            }),
            assoc(
              "entity",
              ((value.insuredEntityDetails &&
                value.insuredEntityDetails.find(x => x.visible)) || {})["value"]
            )
          )
        )
      )(value),
  }
);

const insuranceCartSchema = {
  cartItems: [insuranceCartItemsSchema],
};

export const Schemas = {
  INSURANCE_PRODUCTS: insuranceProductsSchema,
  INSURANCE_PRODUCTS_LIST: [insuranceProductSchema],
  INSURANCE_PRODUCT_FORM: insuranceProductFormSchema,
  INSURANCE_PRODUCT_STATEMENT: null,
  INSURANCE_CART: insuranceCartSchema,
  INSURANCE_CART_ITEM: insuranceCartItemsSchema,
};

// Action key that carries API call info interpreted by this Redux middleware.
export const CALL_API = "Call API";

// A Redux middleware that interprets actions with CALL_API info specified.
// Performs the call and promises when such actions are dispatched.
export default store => next => action => {
  const callAPI = action && action[CALL_API];
  if (typeof callAPI === "undefined") {
    return next(action);
  }

  let { endpoint } = callAPI;
  const {
    schema,
    types,
    payload,
    method = "get",
    mock,
    postApiCallHook,
    postNormalizationHook,
  } = callAPI;

  if (typeof endpoint === "function") {
    endpoint = endpoint(store.getState());
  }

  if (typeof endpoint !== "string") {
    throw new Error("Specify a string endpoint URL.");
  }

  if (!Array.isArray(types) || types.length !== 3) {
    throw new Error("Expected an array of three action types.");
  }
  if (!types.every(type => typeof type === "string")) {
    throw new Error("Expected action types to be strings.");
  }

  const actionWith = data => {
    const finalAction = Object.assign({}, action, data);
    delete finalAction[CALL_API];
    return finalAction;
  };

  const [requestType, successType, failureType] = types;
  next(actionWith({ type: requestType }));

  if (mock) {
    return next(
      actionWith({
        payload: mock,
        type: successType,
      })
    );
  }

  return callApi(
    method,
    endpoint,
    payload,
    schema,
    postApiCallHook,
    postNormalizationHook
  ).then(
    response =>
      next(
        actionWith({
          payload: response,
          type: successType,
        })
      ),
    error =>
      next(
        actionWith({
          type: failureType,
          error: error.message || "Something bad happened",
        })
      )
  );
};



// WEBPACK FOOTER //
// ./api/api.js